import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { productsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, ExternalLink } from 'lucide-react';

const emptyProduct = {
  title: '',
  subtitle: '',
  bottles: 1,
  days: 30,
  price: 0,
  original_price: 0,
  savings: '',
  guarantee: '60 DAY GUARANTEE',
  features: [],
  is_featured: false,
  free_shipping: false,
  bonus_text: '',
  buy_link: '',
  image_url: '',
  order: 0,
};

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [featuresText, setFeaturesText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (e) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setFeaturesText('');
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData(product);
    setFeaturesText(product.features?.join('\n') || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...formData,
        features: featuresText.split('\n').filter((f) => f.trim()),
      };

      if (editingProduct) {
        await productsAPI.update(editingProduct.id, data);
        toast.success('Product updated!');
      } else {
        await productsAPI.create(data);
        toast.success('Product created!');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (e) {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Product deleted!');
      fetchProducts();
    } catch (e) {
      toast.error('Failed to delete product');
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Products</h1>
          <p className="mt-2 text-slate-600">Manage pricing packages and affiliate links</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
              <DialogDescription>Configure product details and pricing</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    placeholder="6 BOTTLES"
                    data-testid="product-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtitle</Label>
                  <Input
                    value={formData.subtitle}
                    onChange={(e) => updateField('subtitle', e.target.value)}
                    placeholder="180 Days, 6 Bottles"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Bottles</Label>
                  <Input
                    type="number"
                    value={formData.bottles}
                    onChange={(e) => updateField('bottles', parseInt(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Days</Label>
                  <Input
                    type="number"
                    value={formData.days}
                    onChange={(e) => updateField('days', parseInt(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price ($)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => updateField('price', parseFloat(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Original ($)</Label>
                  <Input
                    type="number"
                    value={formData.original_price}
                    onChange={(e) => updateField('original_price', parseFloat(e.target.value))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Savings Text</Label>
                  <Input
                    value={formData.savings}
                    onChange={(e) => updateField('savings', e.target.value)}
                    placeholder="YOU SAVE $300"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Guarantee Text</Label>
                  <Input
                    value={formData.guarantee}
                    onChange={(e) => updateField('guarantee', e.target.value)}
                    placeholder="60 DAY GUARANTEE"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Buy Link (Affiliate URL)</Label>
                <Input
                  value={formData.buy_link}
                  onChange={(e) => updateField('buy_link', e.target.value)}
                  placeholder="https://affiliate-link.com/checkout"
                  data-testid="product-buylink-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => updateField('image_url', e.target.value)}
                  placeholder="https://example.com/product.jpg"
                />
              </div>
              <div className="space-y-2">
                <Label>Features (one per line)</Label>
                <Textarea
                  value={featuresText}
                  onChange={(e) => setFeaturesText(e.target.value)}
                  placeholder="Best value bundle&#10;Free shipping included&#10;Bonus ebooks"
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bonus Text</Label>
                  <Input
                    value={formData.bonus_text}
                    onChange={(e) => updateField('bonus_text', e.target.value)}
                    placeholder="+2 FREE EBOOKS"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => updateField('order', parseInt(e.target.value))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => updateField('is_featured', checked)}
                  />
                  <Label>Featured (Best Value)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.free_shipping}
                    onCheckedChange={(checked) => updateField('free_shipping', checked)}
                  />
                  <Label>Free Shipping</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">
                  {saving ? 'Saving...' : 'Save Product'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <Card key={product.id} className={product.is_featured ? 'ring-2 ring-red-500' : ''}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {product.title}
                  </CardTitle>
                  <CardDescription>{product.subtitle}</CardDescription>
                </div>
                {product.is_featured && (
                  <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">
                    FEATURED
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">${product.price}</span>
                  <span className="text-slate-500 line-through">${product.original_price}</span>
                </div>
                <p className="text-sm text-green-600 font-medium">{product.savings}</p>
                {product.buy_link && (
                  <a
                    href={product.buy_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {product.buy_link.substring(0, 40)}...
                  </a>
                )}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => openEditDialog(product)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(product.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminProducts;
